// src/components/flight-data/FreightManifestTab.js
import React, { useState } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  TextField,
  InputAdornment,
  IconButton,
  Collapse,
  Alert
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

function FreightManifestTab({ freightManifestData }) {
  const [showRawText, setShowRawText] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);

  if (!freightManifestData) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">
          No freight manifest data available
        </Alert>
      </Box>
    );
  }


  const formatFreightSection = (sectionName, items) => {
    if (!items || items.length === 0) return null;

    return (
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2, color: '#4CAF50' }}>
          {sectionName}
        </Typography>
        <TableContainer component={Paper} sx={{ backgroundColor: 'rgba(30, 30, 30, 0.7)' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Origin</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Dest</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Air Waybill</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Lot #</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Pcs</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Wt</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Vol</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Description</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item, index) => (
                <TableRow key={index}>
                  <TableCell sx={{ color: '#e2e2e2' }}>{item.origin}</TableCell>
                  <TableCell sx={{ color: '#e2e2e2' }}>{item.destination}</TableCell>
                  <TableCell sx={{ color: '#e2e2e2' }}>{item.air_waybill}</TableCell>
                  <TableCell sx={{ color: '#e2e2e2' }}>{item.lot_number}</TableCell>
                  <TableCell sx={{ color: '#e2e2e2' }}>{item.pieces}</TableCell>
                  <TableCell sx={{ color: '#e2e2e2' }}>{item.weight}</TableCell>
                  <TableCell sx={{ color: '#e2e2e2' }}>{item.volume}</TableCell>
                  <TableCell sx={{ color: '#e2e2e2' }}>{item.description}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Flight Info Header */}
      {freightManifestData.flight_info && (
        <Box sx={{ mb: 3, p: 2, backgroundColor: 'rgba(76, 175, 80, 0.1)', borderRadius: 1 }}>
          <Typography variant="h5" sx={{ mb: 1, color: '#4CAF50' }}>
            Flight {freightManifestData.flight_info.flight_number} - {freightManifestData.flight_info.date}
          </Typography>
          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
            {freightManifestData.flight_info.origin && freightManifestData.flight_info.destination && (
              <Typography sx={{ color: '#e2e2e2' }}>
                Route: {freightManifestData.flight_info.origin} → {freightManifestData.flight_info.destination}
              </Typography>
            )}
            {freightManifestData.flight_info.eta && (
              <Typography sx={{ color: '#e2e2e2' }}>
                ETA: {freightManifestData.flight_info.eta}
              </Typography>
            )}
            {freightManifestData.flight_info.gate && (
              <Typography sx={{ color: '#e2e2e2' }}>
                Gate: {freightManifestData.flight_info.gate}
              </Typography>
            )}
            {freightManifestData.flight_info.tail && (
              <Typography sx={{ color: '#e2e2e2' }}>
                Tail: {freightManifestData.flight_info.tail}
              </Typography>
            )}
            {freightManifestData.flight_info.aircraft_type && (
              <Typography sx={{ color: '#e2e2e2' }}>
                Aircraft: {freightManifestData.flight_info.aircraft_type}
              </Typography>
            )}
          </Box>
        </Box>
      )}

      {/* Freight Sections */}
      {formatFreightSection('Terminating (Local)', freightManifestData.terminating)}
      {formatFreightSection('Transfer', freightManifestData.transfer)}
      {formatFreightSection('Through', freightManifestData.through)}

      {/* Totals */}
      {freightManifestData.totals && (
        <Box sx={{ mb: 3, p: 2, backgroundColor: 'rgba(76, 175, 80, 0.1)', borderRadius: 1 }}>
          <Typography variant="h6" sx={{ mb: 1, color: '#4CAF50' }}>
            Flight Totals
          </Typography>
          <Box sx={{ display: 'flex', gap: 3 }}>
            <Typography sx={{ color: '#e2e2e2' }}>
              Pieces: <strong>{freightManifestData.totals.pieces || 0}</strong>
            </Typography>
            <Typography sx={{ color: '#e2e2e2' }}>
              Weight: <strong>{freightManifestData.totals.weight || 0} lbs</strong>
            </Typography>
            <Typography sx={{ color: '#e2e2e2' }}>
              Volume: <strong>{freightManifestData.totals.volume || 0} ft³</strong>
            </Typography>
          </Box>
        </Box>
      )}

      {/* Parsed Data Section */}
      <Box sx={{ mt: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <IconButton
            onClick={() => setShowRawText(!showRawText)}
            size="small"
            sx={{ color: '#e2e2e2', mr: 1 }}
          >
            {showRawText ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
          <Typography variant="h6" sx={{ color: '#4CAF50' }}>
            Parsed Freight Manifest Data
          </Typography>
        </Box>
        
        <Collapse in={showRawText}>
          <Box sx={{ position: 'relative' }}>
            <Box sx={{ 
              position: 'relative',
              backgroundColor: 'rgba(0, 0, 0, 0.3)',
              borderRadius: 1,
              border: '1px solid rgba(255, 255, 255, 0.23)',
              p: 2,
              fontFamily: 'monospace',
              fontSize: '0.85rem',
              color: '#e2e2e2',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              maxHeight: '80vh',
              overflow: 'auto'
            }}>
              <IconButton
                onClick={() => {
                  const { raw_text, ...dataWithoutRaw } = freightManifestData;
                  navigator.clipboard.writeText(JSON.stringify(dataWithoutRaw, null, 2));
                  setCopySuccess(true);
                  setTimeout(() => setCopySuccess(false), 2000);
                }}
                sx={{ 
                  position: 'absolute', 
                  top: 8, 
                  right: 8,
                  color: copySuccess ? '#4CAF50' : '#e2e2e2',
                  backgroundColor: 'rgba(0, 0, 0, 0.5)',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                  }
                }}
              >
                <ContentCopyIcon />
              </IconButton>
              {(() => {
                // Create a copy without raw_text
                const { raw_text, ...dataWithoutRaw } = freightManifestData;
                return JSON.stringify(dataWithoutRaw, null, 2);
              })()}
            </Box>
            {copySuccess && (
              <Alert 
                severity="success" 
                sx={{ 
                  position: 'absolute', 
                  top: 10, 
                  right: 50,
                  zIndex: 1
                }}
              >
                Copied to clipboard!
              </Alert>
            )}
          </Box>
        </Collapse>
      </Box>
    </Box>
  );
}

export default FreightManifestTab;